import { fieldSeatsForMode, type OnlineRoom, type RoomChatEntry, type RoomMoveTarget } from "@golazo/shared";
import { t } from "../i18n/index.js";
import type { RoomChat } from "../online/roomChat.js";
import { chatLineNode } from "./roomChatView.js";
import "./matchChat.css";

/**
 * El chat de la sala dentro del partido.
 *
 * Como en HaxBall: los mensajes asoman un momento en una esquina y se van
 * solos, sin tapar el campo; ENTER abre la linea para escribir, ENTER la manda
 * y ESC la cierra. En tactil, un boton hace lo que ENTER.
 *
 * Mientras se escribe el jugador se queda quieto (el teclado del juego ignora
 * las teclas que van a un campo de texto): es un juego de pies, no se puede
 * hacer las dos cosas a la vez, y soltar el control es mejor que convertir
 * "hola" en una carrera hacia la izquierda.
 */
export interface MatchChatHandle {
  dispose(): void;
}

/** Cuanto se queda un mensaje a la vista antes de irse. */
const LINE_LIFE_MS = 8000;
/** Cuantos se ven a la vez con la linea cerrada. */
const MAX_VISIBLE = 5;
/** Se recuerda entre partidos: quien lo oculta, lo quiere oculto siempre. */
const HIDDEN_KEY = "fynta.matchChatHidden";

function loadHidden(): boolean {
  try {
    return localStorage.getItem(HIDDEN_KEY) === "1";
  } catch {
    return false;
  }
}

function saveHidden(on: boolean): void {
  try {
    localStorage.setItem(HIDDEN_KEY, on ? "1" : "0");
  } catch {
    // Sin almacenamiento: vale para este partido.
  }
}

/**
 * Lo que el anfitrion puede hacer sin salir del partido. En HaxBall el que
 * manda la sala la manda tambien jugando: meter al que espera o parar. Aqui,
 * igual — si no, para meter a alguien tendria que abandonar el partido.
 */
export interface MatchRoomControls {
  /** La sala tal y como la ha mandado el servidor por ultima vez. */
  room(): OnlineRoom | null;
  move(clientId: string, to: RoomMoveTarget): void;
  stop(): void;
  /** Se ha repintado la sala: el panel, si esta abierto, se actualiza. */
  onChange(fn: () => void): () => void;
}

export function createMatchChat(
  root: HTMLElement,
  opts: { chat: RoomChat; selfId: () => string; touch: boolean; room?: MatchRoomControls },
): MatchChatHandle {
  const box = document.createElement("div");
  box.className = "match-chat";
  box.innerHTML = `
    <div class="match-chat-bar">
      <button type="button" class="match-chat-toggle" data-chat-toggle>
        <span aria-hidden="true">💬</span>
        <span class="match-chat-label">${t("Chat")}</span>
        <span class="match-chat-unread" data-chat-unread hidden></span>
        ${opts.touch ? "" : `<kbd>${t("Enter")}</kbd>`}
      </button>
      <button type="button" class="match-chat-mute" data-chat-mute></button>
      <button type="button" class="match-chat-room" data-room-toggle hidden>${t("Sala")}</button>
    </div>
    <div class="match-room-panel is-hidden" data-room-panel></div>
    <div class="match-chat-feed" data-chat-feed aria-live="polite"></div>
    <form class="match-chat-form is-hidden" data-chat-form>
      <input
        type="text"
        class="match-chat-input"
        data-chat-input
        maxlength="140"
        autocomplete="off"
        enterkeyhint="send"
        placeholder="${t("Escribe a la sala… (Esc para cerrar)")}"
        aria-label="${t("Mensaje para la sala")}"
      />
    </form>
  `;
  root.appendChild(box);

  const toggle = box.querySelector("[data-chat-toggle]") as HTMLButtonElement;
  const feed = box.querySelector("[data-chat-feed]") as HTMLElement;
  const form = box.querySelector("[data-chat-form]") as HTMLFormElement;
  const input = box.querySelector("[data-chat-input]") as HTMLInputElement;
  const muteBtn = box.querySelector("[data-chat-mute]") as HTMLButtonElement;
  const unreadBadge = box.querySelector("[data-chat-unread]") as HTMLElement;
  const timers = new Set<number>();
  let open = false;
  /**
   * Chat oculto: no asoma ningun mensaje sobre el campo. Queda el boton, con
   * cuantos te has perdido, por si quieres mirar — y abrir la linea sigue
   * funcionando: ocultar es no querer que te interrumpan, no quedarse mudo.
   */
  let hidden = loadHidden();
  let unread = 0;

  const syncHidden = (): void => {
    box.classList.toggle("is-muted", hidden);
    // Ojo abierto = se ven los mensajes; tachado = ocultos.
    const eye = hidden
      ? `<svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 3l18 18M10.6 5.1A9.8 9.8 0 0 1 12 5c5 0 9 4.5 10 7-.4 1-1.3 2.4-2.6 3.7M6.2 6.3C4.2 7.6 2.7 9.6 2 12c1 2.5 5 7 10 7 1.8 0 3.4-.6 4.8-1.4"/></svg>`
      : `<svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2"><path d="M2 12c1-2.5 5-7 10-7s9 4.5 10 7c-1 2.5-5 7-10 7S3 14.5 2 12z"/><circle cx="12" cy="12" r="3"/></svg>`;
    muteBtn.innerHTML = `${eye}<span class="match-chat-mute-text">${hidden ? t("Mostrar") : t("Ocultar")}</span>`;
    muteBtn.title = hidden ? t("Mostrar los mensajes del chat") : t("Ocultar los mensajes del chat");
    muteBtn.setAttribute("aria-pressed", String(hidden));
    unreadBadge.hidden = !hidden || unread === 0;
    unreadBadge.textContent = unread > 9 ? "9+" : String(unread);
  };
  syncHidden();

  const addLine = (entry: RoomChatEntry): void => {
    const line = chatLineNode(entry, opts.selfId(), "p");
    feed.appendChild(line);
    while (feed.childElementCount > MAX_VISIBLE * 3) feed.firstElementChild?.remove();
    const timer = window.setTimeout(() => {
      timers.delete(timer);
      line.classList.add("is-gone");
    }, LINE_LIFE_MS);
    timers.add(timer);
  };

  /** Con la linea abierta se ven los ultimos, aunque ya se hubieran ido. */
  const showRecent = (): void => {
    feed.replaceChildren(...opts.chat.entries().slice(-MAX_VISIBLE).map((e) => chatLineNode(e, opts.selfId(), "p")));
  };

  const setOpen = (next: boolean): void => {
    open = next;
    form.classList.toggle("is-hidden", !next);
    box.classList.toggle("is-open", next);
    if (next) {
      unread = 0;
      syncHidden();
      showRecent();
      input.focus({ preventScroll: true });
    } else {
      input.value = "";
      input.blur();
      // De vuelta al campo: el foco al lienzo, que es donde escucha el juego.
      (document.querySelector("canvas") as HTMLElement | null)?.focus({ preventScroll: true });
      for (const line of feed.children) line.classList.add("is-gone");
    }
  };

  const unsub = opts.chat.subscribe((_entries, added) => {
    if (hidden && !open) {
      unread += added.filter((e) => !e.event).length;
      syncHidden();
      return;
    }
    for (const entry of added) addLine(entry);
  });

  muteBtn.addEventListener("click", () => {
    hidden = !hidden;
    saveHidden(hidden);
    if (hidden) for (const line of feed.children) line.classList.add("is-gone");
    unread = 0;
    syncHidden();
  });

  form.addEventListener("submit", (event) => {
    event.preventDefault();
    opts.chat.send(input.value);
    setOpen(false);
  });

  input.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      setOpen(false);
    }
  });

  toggle.addEventListener("click", () => setOpen(!open));

  // --- Panel de sala del anfitrion ------------------------------------
  const roomToggle = box.querySelector("[data-room-toggle]") as HTMLButtonElement;
  const roomPanel = box.querySelector("[data-room-panel]") as HTMLElement;
  let roomOpen = false;
  let stopArmed = false;

  const renderRoom = (): void => {
    const room = opts.room?.room() ?? null;
    const isHost = room !== null && room.hostId === opts.selfId();
    roomToggle.hidden = !isHost;
    const waiting = room?.bench?.length ?? 0;
    roomToggle.textContent = waiting > 0 ? `${t("Sala")} · ${waiting}` : t("Sala");
    if (!isHost || !roomOpen || !room) {
      roomPanel.classList.add("is-hidden");
      return;
    }
    roomPanel.classList.remove("is-hidden");
    const seats = fieldSeatsForMode(room.modeId);
    const free = (side: "home" | "away") => seats.some((st) => st.side === side && !room.seats[st.playerId]);
    const canEnter = room.joinable !== false;
    roomPanel.replaceChildren();
    const head = document.createElement("p");
    head.className = "match-room-head";
    head.textContent = waiting > 0 ? t("Banquillo") : t("Nadie en el banquillo");
    roomPanel.appendChild(head);
    for (const b of room.bench ?? []) {
      const row = document.createElement("div");
      row.className = "match-room-row";
      const name = document.createElement("span");
      name.className = "match-room-nick";
      name.textContent = b.nickname;
      row.appendChild(name);
      for (const side of ["home", "away"] as const) {
        if (!canEnter || !free(side)) continue;
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "match-room-act";
        btn.textContent = side === "home" ? t("L") : t("V");
        btn.title = side === "home" ? t("Meter en Local") : t("Meter en Visitante");
        btn.addEventListener("click", () => opts.room?.move(b.clientId, side));
        row.appendChild(btn);
      }
      roomPanel.appendChild(row);
    }
    if (waiting > 0 && !canEnter) {
      const note = document.createElement("p");
      note.className = "match-room-note";
      note.textContent = t("El partido va muy avanzado: entran en el siguiente.");
      roomPanel.appendChild(note);
    }
    // Parar pide confirmacion: es para todos, y un toque sin querer en movil
    // tiraria el partido de seis personas.
    const stop = document.createElement("button");
    stop.type = "button";
    stop.className = `match-room-stop${stopArmed ? " is-armed" : ""}`;
    stop.textContent = stopArmed ? t("¿Seguro? Pulsa otra vez") : t("Parar partido");
    stop.addEventListener("click", () => {
      if (!stopArmed) {
        stopArmed = true;
        renderRoom();
        window.setTimeout(() => {
          stopArmed = false;
          renderRoom();
        }, 3000);
        return;
      }
      opts.room?.stop();
    });
    roomPanel.appendChild(stop);
  };

  roomToggle.addEventListener("click", () => {
    roomOpen = !roomOpen;
    renderRoom();
  });
  const unsubRoom = opts.room?.onChange(renderRoom) ?? null;
  renderRoom();

  const onKey = (event: KeyboardEvent): void => {
    if (event.key !== "Enter" || open) return;
    // No robar el Enter a otro campo de texto (el de la sala, si lo hubiera).
    const target = event.target as HTMLElement | null;
    if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA")) return;
    event.preventDefault();
    setOpen(true);
  };
  window.addEventListener("keydown", onKey);

  return {
    dispose() {
      unsub();
      unsubRoom?.();
      window.removeEventListener("keydown", onKey);
      for (const timer of timers) clearTimeout(timer);
      timers.clear();
      box.remove();
    },
  };
}
